import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Headphones, MessageSquareText, ShieldCheck, Users } from 'lucide-react';
import ConversationList from './ConversationList';
import ConversationThread from './ConversationThread';
import { createOrGetSupportConversation } from './api';

const SUPPORT_PARAMS = { conversation_type: 'support_general' };
const SUPPORT_FILTERS = [
  ['all', 'Todos'],
  ['unread', 'Sin leer'],
];

export default function SupportCommunicationInbox({
  userId,
  setActiveComponent,
  scope = 'store',
  backTarget = 'Options',
}) {
  const [selected, setSelected] = useState(null);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const [error, setError] = useState(null);
  const currentUserType = scope === 'provider' ? 'provider' : 'store';

  const ensureSupportConversation = useCallback(async () => {
    if (!userId) return;
    setIsEnsuring(true);
    setError(null);
    try {
      const conversation = await createOrGetSupportConversation(currentUserType, userId);
      setSelected(conversation);
    } catch (err) {
      console.error('Support conversation error:', err);
      setError('No se pudo abrir el canal directo de soporte.');
    } finally {
      setIsEnsuring(false);
    }
  }, [currentUserType, userId]);

  useEffect(() => {
    ensureSupportConversation();
  }, [ensureSupportConversation]);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-slate-100">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <button
          onClick={() => setActiveComponent?.(backTarget)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200"
        >
          <ArrowLeft className="h-4 w-4" />
          Menú
        </button>
        <div>
          <h1 className="text-lg font-extrabold text-slate-950 font-montserrat">Soporte Fast Max</h1>
          <p className="text-xs text-slate-500">
            Canal interno directo con administradores para consultas operativas y comerciales.
          </p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className={`${selected ? 'hidden sm:block' : 'block'} w-full sm:w-[430px]`}>
          <ConversationList
            scope={currentUserType}
            userId={userId}
            selectedConversationId={selected?.id}
            onSelect={setSelected}
            title="Soporte directo"
            subtitle="Un hilo interno con Fast Max Admin"
            extraParams={SUPPORT_PARAMS}
            filterOptions={SUPPORT_FILTERS}
            limit={25}
            searchPlaceholder="Buscar mensajes de soporte"
          />
        </div>

        <div className={`${selected ? 'flex' : 'hidden sm:flex'} min-w-0 flex-1 flex-col gap-3 p-4`}>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <Headphones className="h-4 w-4 text-[#B1336E]" />
                Soporte directo
              </div>
              Este hilo conecta tu cuenta con administradores de Fast Max sin salir de la app.
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <MessageSquareText className="h-4 w-4 text-sky-600" />
                Historial interno
              </div>
              Tus consultas quedan centralizadas para seguimiento, respuesta y auditoría operativa.
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                {currentUserType === 'provider' ? (
                  <Users className="h-4 w-4 text-emerald-600" />
                ) : (
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                )}
                Respuesta admin
              </div>
              El equipo Fast Max puede priorizar y responder desde Communication Hub.
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {isEnsuring && !selected ? (
            <div className="flex min-h-[420px] flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white text-sm text-slate-500">
              Abriendo canal de soporte...
            </div>
          ) : (
            <ConversationThread
              conversationId={selected?.id}
              currentUserId={userId}
              currentUserType={currentUserType}
              className="min-h-0 flex-1"
              allowNotes={false}
              inputPlaceholder="Escribe tu consulta para soporte Fast Max..."
              inputHelperText="Mensaje interno: administradores de Fast Max podrán verlo y responder por este mismo canal. No envía WhatsApp."
            />
          )}
        </div>
      </div>
    </div>
  );
}
